import { useQuery } from "@tanstack/react-query"
import { START_PAYLOAD_PATTERN } from "@velizhanin-dev/bot-scenario"
import { useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import { toast } from "sonner"
import type { DraftEntryLink, EntryLinkInput, ScenarioDraft } from "@/entities/bot-builder"
import { getEntryLinkStats, statsKeys } from "@/entities/bot-stats"
import type { DraftActions } from "@/features/edit-scenario-draft"
import { useConfirm } from "@/shared/ui/confirm-provider"
import { makeCode } from "./make-code"

const EMPTY_FORM: Required<EntryLinkInput> = {
	code: "",
	name: "",
	comment: "",
	flowId: null,
	addTagIds: [],
	active: true,
}

export type UtmSortKey = "name" | "count" | "finished" | "lastAt"

export function useUtmLinks({
	draft,
	actions,
	botId,
	botUsername,
}: {
	draft: ScenarioDraft
	actions: DraftActions
	botId: string
	botUsername: string
}) {
	const { confirm } = useConfirm()
	const navigate = useNavigate()
	const [search, setSearch] = useState("")
	const [editing, setEditing] = useState<{
		link?: DraftEntryLink
		form: Required<EntryLinkInput>
		codeEdited: boolean
	} | null>(null)

	const stats = useQuery({
		queryKey: statsKeys.entryLinks(botId),
		queryFn: () => getEntryLinkStats(botId),
	})
	const statsByCode = useMemo(
		() => new Map((stats.data?.links ?? []).map((row) => [row.code, row])),
		[stats.data],
	)
	const [sort, setSort] = useState<{ key: UtmSortKey; desc: boolean }>({ key: "count", desc: true })
	const sortBy = (key: UtmSortKey) =>
		setSort((current) => ({ key, desc: current.key === key ? !current.desc : key !== "name" }))
	const flowName = (flowId: string | null) =>
		flowId
			? (draft.flows.find((flow) => flow.id === flowId)?.name ?? "удалённая воронка")
			: "Основная воронка"
	const linkUrl = (code: string) => `https://t.me/${botUsername}?start=${code}`

	const query = search.trim().toLocaleLowerCase()
	const sortValue = (link: DraftEntryLink): number | string => {
		const row = statsByCode.get(link.code)
		switch (sort.key) {
			case "name":
				return link.name.toLocaleLowerCase()
			case "count":
				return row?.count ?? 0
			case "finished":
				return row && row.count > 0 ? row.finished / row.count : -1
			case "lastAt":
				return row ? Date.parse(row.lastAt) : 0
		}
	}
	const links = (draft.entryLinks ?? [])
		.filter(
			(link) =>
				!query ||
				link.code.toLocaleLowerCase().includes(query) ||
				link.name.toLocaleLowerCase().includes(query),
		)
		.sort((a, b) => {
			const left = sortValue(a)
			const right = sortValue(b)
			const order = left < right ? -1 : left > right ? 1 : 0
			return sort.desc ? -order : order
		})

	const form = editing?.form
	const codeValid = !!form && START_PAYLOAD_PATTERN.test(form.code)
	const save = async () => {
		if (!editing || !codeValid || !editing.form.name.trim()) return
		const input: EntryLinkInput = {
			...editing.form,
			code: editing.form.code.trim(),
			name: editing.form.name.trim(),
			comment: editing.form.comment?.trim() || null,
		}
		const result = editing.link
			? await actions.updateEntryLink(editing.link.id, input)
			: await actions.createEntryLink(input)
		if (result) setEditing(null)
	}

	const setForm = (patch: Partial<Required<EntryLinkInput>>) =>
		setEditing((current) =>
			current
				? {
						...current,
						codeEdited: patch.code === undefined ? current.codeEdited : patch.code !== "",
						form: { ...current.form, ...patch },
					}
				: current,
		)

	const setName = (name: string) =>
		setEditing((current) => {
			if (!current) return current
			const form = { ...current.form, name }
			if (!current.link && !current.codeEdited) {
				const taken = new Set((draft.entryLinks ?? []).map((link) => link.code))
				form.code = makeCode(name, taken)
			}
			return { ...current, form }
		})

	const openCreate = () => setEditing({ form: EMPTY_FORM, codeEdited: false })
	const openEdit = (link: DraftEntryLink) =>
		setEditing({
			link,
			codeEdited: true,
			form: {
				code: link.code,
				name: link.name,
				comment: link.comment ?? "",
				flowId: link.flowId,
				addTagIds: link.addTagIds,
				active: link.active,
			},
		})
	const closeEditing = () => setEditing(null)

	const copyLink = (code: string) =>
		void navigator.clipboard
			.writeText(linkUrl(code))
			.then(() => toast.success("Ссылка скопирована"))

	const openPeople = (code: string) =>
		navigate(`/admin/bots/${botId}/analytics/utm/${encodeURIComponent(code)}`)

	const setActive = (link: DraftEntryLink, active: boolean) =>
		void actions.updateEntryLink(link.id, { active })

	const deleteLink = (link: DraftEntryLink) =>
		confirm({
			title: `Удалить метку ${link.code}?`,
			message: "Ссылка с этой меткой будет запускать основную воронку без группы.",
			confirmText: "Удалить",
			onConfirm: async () => {
				await actions.deleteEntryLink(link.id)
			},
		})

	return {
		draft,
		search,
		setSearch,
		query,
		links,
		editing,
		form,
		codeValid,
		stats,
		statsByCode,
		sort,
		sortBy,
		flowName,
		linkUrl,
		save,
		setForm,
		setName,
		openCreate,
		openEdit,
		closeEditing,
		copyLink,
		openPeople,
		setActive,
		deleteLink,
	}
}

export type UtmLinksState = ReturnType<typeof useUtmLinks>
